import {
    IonAlert,
    IonCol,
    IonLoading,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonToast,
    useIonViewWillEnter,
} from "@ionic/react";
import React, {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import StaticPane from "../../../components/pane/static-pane.component";
import PageLeavingGuard from "../../../components/page-leaving/pageLeavingGuard.component";
import {StyledInconsitencyItemDetailsContent} from "./inconcistencyModule.style";
import Accordion from "../components/form/accordion";
import {StyledAccordion} from "../../reportPage/reportPage.style";
import {
    StyledButton, StyledButtonBadge,
    StyledCircleButton,
    StyledLightBlueButton,
    StyledSocialButton
} from "@components/button/button.style";
import {StyledTextarea} from "@components/form/input/textarea.style";
import {AuditReportContext} from "@app/auditReport/auditReport.component";
import {AuditReportFormModel, InconsistencyNewRecordFormModel, InconsistencyRecordAction} from "@models/auditReport";
import {
    getCompanies,
    getInconsistencyUsers, getPlaceOfServiceOrders,
    getPlaceOfServices,
    postNewInconsistencyRecord
} from "@services/audit.service";
import {Links} from "@app/links";
import {StyledIonSelect} from "@components/form/input/select.style";
import TrashRedImage from "@assets/images/trash-can-red.svg";
import {ActionTypes, AuditType} from "@enums/audit";
import {RemoveActionState} from "@app/inconsistencyModulePage/panes/inconcistencyModuleEditPane.component";
import {StyledInput} from "@components/form/input/input.style";
import PaginatedListModal from "@app/auditReport/modals/paginatedListModal.component";
import PaginatedListModalComponent from "@components/modal/paginatedListModal.component";

import {DataSelectable, File} from "@models/common";
import DateInput from "@components/form/input/date.component";
import PhotosImage from "@assets/images/photos.svg";
import AuditInconcistencyFilesModal from "@app/inconsistencyModulePage/modals/filesModal.component";

const MomentRange = require('moment-range');
const Moment = require('moment-timezone')
const moment = MomentRange.extendMoment(Moment);
const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);

type InconsistencyModuleNewProps = {
    topEdge: number | undefined;
}

const InconsistencyModuleNewPane: React.FC<InconsistencyModuleNewProps> = (props: InconsistencyModuleNewProps) => {
    const {t} = useTranslation();

    const history = useHistory();

    const [newRecordFormModel, setNewRecordFormModel] = useState<InconsistencyNewRecordFormModel>({
        detection_reason: undefined,
        actions: [],
        user: undefined,
        comment: undefined,
        type: undefined,
        actionTerm: moment().format('YYYY-MM-DD'),
        placeOfService: undefined,
        order: undefined,
        files: [],
    });

    const [showToast, setShowToast] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState<any>(false);
    const [formSaved, setFormSaved] = useState(false);
    const [showUsersModal, updateShowUsersModal] = useState<boolean>(false);
    const [showPlaceOfServiceModal, updateShowPlaceOfServiceModal] = useState<boolean>(false);
    const [showCompaniesModal, updateShowCompaniesModal] = useState<boolean>(false);
    const [showOrdersModal, updateShowOrdersModal] = useState<boolean>(false);
    const [showFilesModal, updateShowFilesModal] = useState<boolean>(false);
    const [hasOneCompany, setHasOneCompany] = useState<boolean>(false);
    const [toast, setToast] = useState<any>('');
    const [showLoading, updateShowLoading] = useState(false);
    const [removeActionState, updateRemoveActionState] = useState({
        showAlert: false,
        action: undefined,
        index: undefined
    } as RemoveActionState);

    const contextAuditReport = useContext(AuditReportContext);
    contextAuditReport.setFormSubmitted = setFormSubmitted;
    contextAuditReport.getFormSubmitted = () => {
        return formSubmitted;
    };

    const auditContext = useContext(AuditReportContext);

    const addNextAction = () => {
        const actions = [...(newRecordFormModel.actions || [])];
        actions.push({type: undefined, comment: ''})
        setNewRecordFormModel({
            ...newRecordFormModel,
            actions: actions
        });
    }

    const selectActionType = (action: InconsistencyRecordAction, value: string) => {
        const index = newRecordFormModel.actions?.indexOf(action)
        if (index !== undefined && index !== -1) {
            const actions = [...(newRecordFormModel.actions || [])];
            actions[index].type = value;
            setNewRecordFormModel({
                ...newRecordFormModel,
                actions: actions
            })
        }
    }

    const updateActionComment = (action: InconsistencyRecordAction, value: string) => {
        const index = newRecordFormModel.actions?.indexOf(action)
        if (index !== undefined && index !== -1) {
            const actions = [...(newRecordFormModel.actions || [])];
            actions[index].comment = value;
            setNewRecordFormModel({
                ...newRecordFormModel,
                actions: actions
            })
        }
    }

    const removeAction = (action: InconsistencyRecordAction) => {
        const index = newRecordFormModel.actions?.indexOf(action)
        if (index !== undefined && index !== -1) {
            const actions = [...(newRecordFormModel.actions || [])];
            actions.splice(index, 1)
            setNewRecordFormModel({
                ...newRecordFormModel,
                actions: actions
            })
        }
    }

    const availableActions = (action: InconsistencyRecordAction) => {
        return ActionTypes.filter((type) => {
            return type === action.type || (!action.type && newRecordFormModel.actions.filter(value => value.type === type).length === 0);
        })
    }

    const onSubmit = async () => {
        setFormSubmitted(true);
        updateShowLoading(true);

        await onSave();
        updateShowLoading(false);
    }

    const onSave = async () => {
        await postNewInconsistencyRecord(newRecordFormModel).then(() => {
            setFormSaved(true);
            history.replace(Links.main + Links.inconsistencyModule);
        }).catch(() => {
            setToast(t("common.serverErrorMsg"));
            setShowToast(true);
        })
    }

    const loadUsers = async (page: number, searchText?: string) => {
        return await getInconsistencyUsers(page, searchText).then((response) => {
            return response.data as DataSelectable[];
        });
    }

    const loadCompanies = async (page: number, searchText?: string) => {
        return await getCompanies(page, searchText).then(response => {
            return response.data as DataSelectable[];
        });
    }

    const initCompany = async () => {
        return await getCompanies(1, '').then(response => {
            if (response.data.length === 1) {
                setNewRecordFormModel({
                    ...newRecordFormModel,
                    company: response.data[0],
                })
                setHasOneCompany(true)
            }
        });
    }

    const loadPlaceOfServices = async (page: number, searchText?: string) => {
        if (newRecordFormModel.company) {
            return await getPlaceOfServices(page, searchText, newRecordFormModel.company.id.toString()).then(response => {
                return response.data as DataSelectable[];
            });
        } else {
            return [];
        }
    }

    const loadOrders = async (page: number, searchText?: string) => {
        if (newRecordFormModel.placeOfService) {
            return await getPlaceOfServiceOrders(newRecordFormModel.placeOfService.id.toString(), page, searchText).then((response) => {
                return response.data as DataSelectable[];
            });
        } else {
            return [];
        }
    }

    const getFiles = () => {
        return newRecordFormModel.files
    }

    const setFiles = (files: File[],) => {
        setNewRecordFormModel({
            ...newRecordFormModel,
            files: files,
        })
    }

    useIonViewWillEnter(() => {
        initCompany();
    });

    const handlePlaceOfServiceChoose = (selectedPlaceOfService: DataSelectable & { manual?: boolean }) => {
        setNewRecordFormModel({
            ...newRecordFormModel,
            placeOfService: !selectedPlaceOfService.manual ? selectedPlaceOfService : undefined,
            placeOfServiceManual: selectedPlaceOfService.manual ? selectedPlaceOfService.name : undefined,
        });
    }

    const getPlaceOfServiceName = () => {
        if (newRecordFormModel?.placeOfServiceManual) {
            return newRecordFormModel.placeOfServiceManual
        }

        if (newRecordFormModel?.placeOfService) {
            return newRecordFormModel.placeOfService.name
       }

        return '';
    }

    return (
        <StaticPane topEdge={props.topEdge} marginTop={40} paddingBottom={147}>
            <StyledInconsitencyItemDetailsContent>
                <div>
                    <Accordion isOpen={true} allowToggle={false} title={'inconsistencyTab.newTitle'}>
                        <StyledAccordion>
                            {
                                <>
                                    <IonRow>
                                        <IonCol size="12" className="label mt-8 required">
                                            {t('inconsistency.form.detectionReason.title')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12">
                                            <StyledTextarea
                                                onKeyPress={event => {
                                                    if (event.key === 'Enter') {
                                                        const input = event.target as HTMLInputElement;
                                                        input.blur();
                                                    }
                                                }}
                                                className={auditContext.getFormSubmitted() && (!newRecordFormModel.detection_reason) ? 'full-width hasErrors' : 'full-width'}
                                                onChange={(e) => {
                                                    setNewRecordFormModel({
                                                        ...newRecordFormModel,
                                                        detection_reason: e.target.value
                                                    })
                                                }}
                                                value={newRecordFormModel.detection_reason}
                                                placeholder={t('inconsistency.form.detectionReason.placeholder')}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow>
                                        <IonCol size="12" className="label required">
                                            {t('inconsistency.form.type')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" className="select-full-width">
                                            <IonSelect
                                                interface="action-sheet"
                                                className={auditContext.getFormSubmitted() && !newRecordFormModel.type ? 'hasErrors' : ''}
                                                onIonChange={(e) => {
                                                    setNewRecordFormModel({
                                                        ...newRecordFormModel,
                                                        type: e.detail.value,
                                                    });
                                                }}
                                                placeholder={t('common.selectPlaceholder')}
                                                okText={t('common.selectOk')}
                                                cancelText={t('common.selectCancel')}
                                                value={newRecordFormModel.type}
                                                interfaceOptions={
                                                    {
                                                        header: t('common.select')
                                                    }
                                                }
                                            >
                                                <IonSelectOption
                                                    value={AuditType.QUALITY}>{t('inconsistency.form.audit_type.quality')}</IonSelectOption>
                                                <IonSelectOption
                                                    value={AuditType.BHP}>{t('inconsistency.form.audit_type.bhp')}</IonSelectOption>
                                                <IonSelectOption
                                                    value={AuditType.OTHER}>{t('inconsistency.form.audit_type.other')}</IonSelectOption>
                                            </IonSelect>
                                        </IonCol>
                                    </IonRow>
                                </>

                            }
                            {
                                <>
                                    <IonRow className="mt-24">
                                        <IonCol size="12" className="label">
                                            {t('inconsistency.form.companies')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" onClick={() => {
                                            updateShowCompaniesModal(true)
                                        }}>
                                            <StyledInput readOnly={true}
                                                         disabled={hasOneCompany}
                                                         value={newRecordFormModel.company ? newRecordFormModel.company.name : ''}
                                                         placeholder={t("common.select")}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow className="mt-24">
                                        <IonCol size="12" className="label">
                                            {t('inconsistency.form.placeOfService')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" onClick={() => {
                                            updateShowPlaceOfServiceModal(true)
                                        }}>
                                            <StyledInput readOnly={true}
                                                         disabled={newRecordFormModel.company === undefined}
                                                         value={getPlaceOfServiceName()}
                                                         placeholder={t("common.select")}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow className="mt-24">
                                        <IonCol size="12" className="label">
                                            {t('inconsistency.form.order')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" onClick={() => {
                                            if (newRecordFormModel.placeOfService) {
                                                updateShowOrdersModal(true)
                                            }
                                        }}>
                                            <StyledInput readOnly={true}
                                                         disabled={newRecordFormModel.placeOfService === undefined}
                                                         value={newRecordFormModel.order ? newRecordFormModel.order.name : ''}
                                                         placeholder={t("common.select")}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow>
                                        <IonCol size="12">
                                            <StyledSocialButton className="report-button black-text"
                                                                onClick={() => updateShowFilesModal(true)}>
                                                <img className="button-image" src={PhotosImage}/>
                                                {t('auditReport.form.addFiles')}
                                                {getFiles().length > 0 && <StyledButtonBadge
                                                    className="badge default-position">{getFiles().length}</StyledButtonBadge>}
                                            </StyledSocialButton>
                                        </IonCol>
                                    </IonRow>
                                </>

                            }
                            {
                                newRecordFormModel.actions.map((action: InconsistencyRecordAction, index: number) => {
                                    return <>
                                        <IonRow>
                                            <IonCol size="12" className="label mt-8">
                                                {t('inconsistency.form.action.title')}
                                            </IonCol>
                                        </IonRow>
                                        <IonRow>
                                            <IonCol size="11" className="select">
                                                <StyledIonSelect
                                                    interface="action-sheet"
                                                    placeholder={t('common.selectPlaceholder')}
                                                    onIonChange={e => {
                                                        selectActionType(action, e.detail.value)
                                                    }}
                                                    okText={t('common.selectOk')}
                                                    cancelText={t('common.selectCancel')}
                                                    interfaceOptions={
                                                        {
                                                            header: t('inconsistency.form.selectAction'),
                                                        }
                                                    }
                                                    value={action.type}
                                                >
                                                    {availableActions(action).map((type, index: number) => {
                                                        return <IonSelectOption
                                                            value={type}>{t('inconsistency.form.actionType.' + type.toLowerCase())}</IonSelectOption>
                                                    })}
                                                </StyledIonSelect>
                                            </IonCol>

                                            <IonCol size="1">
                                                <StyledCircleButton className="white"
                                                                    onClick={() => updateRemoveActionState({
                                                                        showAlert: true,
                                                                        action: action,
                                                                        index: index
                                                                    })}><img
                                                    src={TrashRedImage}/></StyledCircleButton>
                                            </IonCol>
                                        </IonRow>
                                        <IonRow>
                                            <IonCol size="12" className="label mt-8">
                                                {t('inconsistency.form.action.comment')}
                                            </IonCol>
                                        </IonRow>
                                        <IonRow>
                                            <IonCol size="12">
                                                <StyledTextarea
                                                    onKeyPress={event => {
                                                        if (event.key == 'Enter') {
                                                            const input = event.target as HTMLInputElement;
                                                            input.blur();
                                                        }
                                                    }}
                                                    className={'full-width'}
                                                    onChange={(e) => {
                                                        updateActionComment(action, e.target.value)
                                                    }}
                                                    value={action.comment}
                                                    placeholder={t('auditReport.form.enter')}/>
                                            </IonCol>
                                        </IonRow>
                                    </>
                                })
                            }

                            <hr className="hr"/>
                            {
                                newRecordFormModel.actions.length < 3 && <IonRow>
                                    <StyledLightBlueButton onClick={() => addNextAction()}>
                                        {t('inconsistency.form.addNextAction')}
                                    </StyledLightBlueButton>
                                </IonRow>
                            }
                            {
                                <>
                                    <IonRow className="mt-24">
                                        <IonCol size="12" className="label">
                                            {t('inconsistency.form.user')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" onClick={() => {
                                            updateShowUsersModal(true)
                                        }}>
                                            <StyledInput readOnly={true}
                                                         className={auditContext.getFormSubmitted() && (!newRecordFormModel.user) ? 'hasErrors' : ''}
                                                         value={newRecordFormModel.user ? newRecordFormModel.user.name : ''}
                                                         placeholder={t("common.select")}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow>
                                        <IonCol size="12" className="label required">
                                            {t('inconsistency.form.term')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12" className="date-field">
                                            <DateInput
                                                presentation="date"
                                                format="DD.MM.YYYY"
                                                value={newRecordFormModel.actionTerm}
                                                onChange={(value) => {
                                                    setNewRecordFormModel({
                                                        ...newRecordFormModel,
                                                        actionTerm: moment(value).format('YYYY-MM-DD')
                                                    });
                                                }}
                                                cancelText={t("common.dateCancel")}
                                                doneText={t("common.dateDone")}
                                                placeholder={t("common.select")}
                                                min={moment().format('YYYY-MM-DD')}
                                                className={auditContext.getFormSubmitted() && !newRecordFormModel.actionTerm ? 'hasErrors' : ''}
                                            />
                                        </IonCol>

                                    </IonRow>
                                </>
                            }
                            {
                                <>
                                    <IonRow>
                                        <IonCol size="12" className="label mt-8 required">
                                            {t('inconsistency.form.comment_label')}
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol size="12">
                                            <StyledTextarea
                                                onKeyPress={event => {
                                                    if (event.key == 'Enter') {
                                                        const input = event.target as HTMLInputElement;
                                                        input.blur();
                                                    }
                                                }}
                                                className="full-width"
                                                onChange={(e) => {
                                                    setNewRecordFormModel({
                                                        ...newRecordFormModel,
                                                        comment: e.target.value
                                                    })
                                                }}
                                                value={newRecordFormModel.comment}
                                                placeholder={t('inconsistency.form.comment')}/>
                                        </IonCol>
                                    </IonRow>
                                </>
                            }
                        </StyledAccordion>
                        <IonRow>
                            <StyledButton onClick={onSubmit}>
                                {t('inconsistency.form.save')}
                            </StyledButton>
                        </IonRow>
                    </Accordion>
                </div>
            </StyledInconsitencyItemDetailsContent>

            {showUsersModal &&
                <PaginatedListModal search={true} report={{} as AuditReportFormModel} isOpen={showUsersModal}
                                    onFetchGeneric={(page: number, searchText?: string) => {
                                        return loadUsers(page, searchText);
                                    }} onSubmit={(selected) => {
                    setNewRecordFormModel({
                        ...newRecordFormModel,
                        user: selected,
                    });

                    updateShowUsersModal(false);
                }} onClose={() => {
                    updateShowUsersModal(false);
                }}/>
            }

            {showCompaniesModal &&
                <PaginatedListModal search={true} report={{} as AuditReportFormModel} isOpen={showCompaniesModal}
                                    onFetchGeneric={(page: number, searchText?: string) => {
                                        return loadCompanies(page, searchText);
                                    }} onSubmit={(selected) => {
                    setNewRecordFormModel({
                        ...newRecordFormModel,
                        company: selected,
                    });

                    updateShowCompaniesModal(false);
                }} onClose={() => {
                    updateShowCompaniesModal(false);
                }}/>
            }

            {showPlaceOfServiceModal &&
                <PaginatedListModalComponent isOpen={showPlaceOfServiceModal}
                                    fetchData={loadPlaceOfServices}
                                    setValue={handlePlaceOfServiceChoose}
                                    allowManual={newRecordFormModel.type === AuditType.OTHER}
                                    onClose={() => updateShowPlaceOfServiceModal(false)}/>
            }

            {showOrdersModal &&
                <PaginatedListModal search={true} report={{} as AuditReportFormModel} isOpen={showOrdersModal}
                                    onFetchGeneric={(page: number, searchText?: string) => {
                                        return loadOrders(page, searchText);
                                    }} onSubmit={(selected) => {
                    setNewRecordFormModel({
                        ...newRecordFormModel,
                        order: selected,
                    });

                    updateShowOrdersModal(false)
                }} onClose={() => {
                    updateShowOrdersModal(false);
                }}/>
            }

            <IonAlert
                isOpen={removeActionState.showAlert}
                onDidDismiss={() => updateRemoveActionState({showAlert: false, action: undefined, index: undefined})}
                header={t('common.alertHeader')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            updateRemoveActionState({showAlert: false, action: undefined, index: undefined})
                        },
                    },
                    {
                        text: t('common.alertConfirm'),
                        handler: async () => {
                            if (removeActionState.action !== undefined) {
                                removeAction(removeActionState.action);
                            }
                        }
                    }
                ]}
            />

            {showFilesModal && <AuditInconcistencyFilesModal files={getFiles()} isOpen={true}
                                                             onClose={() => updateShowFilesModal(false)}
                                                             onSubmit={(files) => {
                                                                 setFiles(files);
                                                                 updateShowFilesModal(false);
                                                             }}/>
            }

            <IonToast
                isOpen={showToast}
                onDidDismiss={() => {
                    setShowToast(false);
                    setToast('');
                }}
                message={toast}
                duration={6000}
                position="top"
                color="danger"
            />
            <IonLoading onDidDismiss={() => updateShowLoading(false)} isOpen={showLoading}/>

            <PageLeavingGuard navigate={path => history.goBack()} shouldBlockNavigation={location => {
                return !formSaved;
            }}/>
        </StaticPane>
    );
};

export default InconsistencyModuleNewPane;
